import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import { useTimelineContext } from "./remotion-timeline/context/use-timeline-context";
import { ItemType, TextItem, TrackType } from "./remotion-timeline/types";
import { DEFAULT_ROTATION, DEFAULT_TEXT_CONFIGURATION } from "./canvas/config";
import useVideo from "../../../../hooks/useVideo";
import useMonitorVideoGeneration from "../../../../hooks/useMonitorVideoGeneration";
import { sendErrorNotification } from "../../../../library/notification";
import useGenerateVideo from "../../../../hooks/useGenerateVideo";
import { ExportError } from "../../../../types";
import { getMediaTypeFromFilename } from "../../../../utils/getMediaTypeFromFilename";

type Alignment = {
  horizontal: string;
  vertical: string;
};

type DimensionSettings = {
  width: number;
  height: number;
};

type ContentSettings = {
  text: string;
};

type PositionSettings = {
  x: number;
  y: number;
};

type RotationSettings = {
  degrees: number;
};

type BackgroundSettings = {
  color: string;
  radius: number;
};

type StrokeSettings = {
  width: number;
  color: string;
};

type TextConfigurationSettings = {
  font: {
    family: string;
    size: number;
    decoration: string;
    variant: string;
    weight: string;
    lineHeight: number;
    color: string;
  };
  background: {
    color: string;
    radius: number;
  };
  stroke: {
    width: number;
    color: string;
  } | null;
  alignment: Alignment;
};
// Combined settings type
type ItemSettings = {
  content: ContentSettings;
  position: PositionSettings;
  rotation: RotationSettings;
  size: DimensionSettings;
  configuration: TextConfigurationSettings;
};

type SettingsContextType = {
  isEditorLoaded: boolean;

  loadEditor: () => void;

  applyToAll: boolean;

  updateContentSettings: (content: Partial<ContentSettings>) => void;

  updatePositionSettings: (position: Partial<PositionSettings>) => void;

  updateStrokeSettings: (stroke: Partial<StrokeSettings>) => void;

  updateRotationSettings: (rotation: Partial<typeof DEFAULT_ROTATION>) => void;

  updateBackgroundSettings: (background: Partial<BackgroundSettings>) => void;

  updateAlignmentSettings: (
    alignment: Partial<{
      horizontal: string;
      vertical: string;
    }>
  ) => void;

  applyToAllMap: Record<string, boolean>; // Track per item ID
  setApplyToAllForCurrentItem: (value: boolean) => void;

  updateFontSettings: (
    fontSettings: Partial<{
      family: string;
      size: number;
      decoration: string;
      lineHeight: number;
      variant: string;
      weight: string;
      color: string;
    }>
  ) => void;

  updateSizeSettings: (
    size: Partial<{
      width: number;
      height: number;
    }>
  ) => void;

  getSelectedItemSettings: () => ItemSettings;

  selectedItemId: string | null;

  fonts: any[];

  bottomPanelHeight: number;

  setBottomPanelHeight: (height: number) => void;

  generateVideo: () => Promise<void>;

  displayError: ExportError | null;

  isGenerating: boolean;

  setIsGenerating: (value: boolean) => void;

  generationProgress: number;

  videoDownloadUrl: string | undefined;
};

const defaultItemSettings: ItemSettings = {
  content: {
    text: "",
  },
  position: {
    x: 0,
    y: 0,
  },
  size: {
    width: 0,
    height: 0,
  },
  rotation: {
    degrees: 0,
  },
  configuration: DEFAULT_TEXT_CONFIGURATION,
};

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

interface SettingsProviderProps {
  children: ReactNode;
  fonts: any[];
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
  fonts = [],
}) => {
  const { monitorVideoGeneration } = useMonitorVideoGeneration();
  const {
    selectedItem,
    changeItem,
    tracks,
    fps,
    addItem,
    addTrack,
    setTracks,
  } = useTimelineContext();
  const { video, saveVideo } = useVideo();
  const { downloadVideo } = useGenerateVideo();
  const isInitialMount = useRef(true);
  const prevTracksStringRef = useRef("");

  const [isEditorLoaded, setIsEditorLoaded] = useState(false);
  const currentVideoId = useRef(video.id);

  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationProgress, setGenerationProgress] = useState<number>(0);
  const [videoDownloadUrl, setVideoDownloadUrl] = useState<
    string | undefined
  >();
  const [pollingIntervalId, setPollingIntervalId] =
    useState<NodeJS.Timeout | null>(null);

  const [displayError, setDisplayError] = useState<ExportError | null>(null);

  const [bottomPanelHeight, setBottomPanelHeight] = useState<number>(0);
  const [applyToAllMap, setApplyToAllMap] = useState<Record<string, boolean>>(
    {}
  );

  const applyToAll = selectedItem ? applyToAllMap[selectedItem] ?? true : true;

  const clearAllTracks = useCallback(() => {
    setTracks([]);
  }, [setTracks]);

  const generateDynamicFontSize = (
    videoWidth: number,
    videoHeight: number,
    captionWidth: number
  ): number => {
    const isPortrait = videoHeight > videoWidth;

    let baseFontSize: number;

    if (isPortrait) {
      baseFontSize = Math.round(videoWidth * 0.07);
    } else {
      const baseCharactersPerLine = 30;
      baseFontSize = Math.round(captionWidth / baseCharactersPerLine);
    }

    const minFontSize = 16;
    const maxFontSize = isPortrait
      ? Math.round(videoWidth * 0.08)
      : Math.round(videoWidth * 0.045);

    return Math.max(minFontSize, Math.min(baseFontSize, maxFontSize));
  };

  const generateCaptionDetails = (
    videoWidth: number,
    videoHeight: number,
    captionWidthPercentage: number = 0.75,
    captionHeightRatio: number = 0.2
  ) => {
    let bottomMargin: number = 0;

    switch (video.format) {
      case "word":
        if (video?.details?.height > video?.details?.width) {
          bottomMargin = 0.225;
        } else {
          bottomMargin = 0.12;
        }
        break;
      case "extra-short":
        if (video?.details?.height > video?.details?.width) {
          bottomMargin = 0.25;
        } else {
          bottomMargin = 0.125;
        }
        break;
      case "short":
        if (video?.details?.height > video?.details?.width) {
          bottomMargin = 0.275;
        } else {
          bottomMargin = 0.135;
        }
        break;
      default:
        if (video?.details?.height > video?.details?.width) {
          bottomMargin = 0.35;
        } else {
          bottomMargin = 0.1;
        }
        break;
    }
    const captionWidth = Math.round(videoWidth * captionWidthPercentage);
    const captionHeight = Math.round(captionWidth * captionHeightRatio);

    const x = Math.round((videoWidth - captionWidth) / 2);

    const bottomSpacing = Math.round(videoHeight * bottomMargin);
    const y = videoHeight - captionHeight - bottomSpacing;

    return {
      position: { x, y },
      size: { width: captionWidth, height: captionHeight },
    };
  };

  const configureNewTimeline = useCallback(
    (durationInFrames: number, fps: number) => {
      const videoWidth = video.details?.width || 1280;
      const videoHeight = video.details?.height || 720;

      const captionDetails = generateCaptionDetails(videoWidth, videoHeight);

      setTracks([]);

      addTrack({
        items: [],
        id: "2",
        name: "Spor 2",
      });

      addTrack({
        items: [],
        id: "1",
        name: "Spor 1",
      });

      if (getMediaTypeFromFilename(video.filename) === "video") {
        addItem(1, {
          id: video.id || "video-main",
          durationInFrames: durationInFrames,
          from: 0,
          isDragging: false,
          type: "video",
          src: video.sourceUrl,
          videoDurationInSeconds: video.duration,
          videoStartFromInSeconds: 0,
          position: {
            x: 0,
            y: 0,
          },
          rotation: {
            degrees: 0,
          },
        });
      }

      if (getMediaTypeFromFilename(video.filename) === "audio") {
        addItem(1, {
          id: video.id || "audio-main",
          durationInFrames: durationInFrames,
          from: 0,
          type: "audio",
          src: video.sourceUrl,
          audioDurationInSeconds: video.duration,
          audioStartFromInSeconds: 0,
        });
      }

      video.content.forEach((contentItem: any, index: number) => {
        const from = Math.round(contentItem.start * fps);
        const durationInSeconds = contentItem.end - contentItem.start;
        let durationInFrames = Math.round(durationInSeconds * fps);

        if (durationInFrames <= 0) {
          durationInFrames = 1;
        }

        addItem(0, {
          id: contentItem.key || `text-${index}`,
          from,
          durationInFrames,
          isDragging: false,
          type: "text",
          text: contentItem.text,
          position: captionDetails.position,
          rotation: DEFAULT_ROTATION,
          configuration: {
            ...DEFAULT_TEXT_CONFIGURATION,
            font: {
              ...DEFAULT_TEXT_CONFIGURATION.font,
              size: generateDynamicFontSize(
                videoWidth,
                videoHeight,
                captionDetails.size.width
              ),
            },
          },
          size: captionDetails.size,
        });
      });
    },
    [
      addItem,
      addTrack,
      setTracks,
      video.content,
      video.details?.height,
      video.details?.width,
      video.duration,
      video.id,
      video.sourceUrl,
    ]
  );

  const configureRevisionTimeline = useCallback(() => {
    clearAllTracks();

    const tracksToAdd = [...video.content];

    tracksToAdd.sort((a, b) => {
      return Number(b.id) - Number(a.id);
    });

    tracksToAdd.forEach((track: TrackType) => {
      const updatedTrack = {
        ...track,
        items: track.items.map((item: ItemType) => {
          if (item.type === "video") {
            return {
              ...item,
              src: video.sourceUrl,
            };
          }

          return item;
        }),
      };

      addTrack(updatedTrack);
    });
  }, [addTrack, clearAllTracks, video.content, video.sourceUrl]);

  const loadEditor = useCallback(() => {
    const isVideoChanged = currentVideoId.current !== video.id;

    currentVideoId.current = video.id;

    if (isVideoChanged) {
      setTracks([]);
      isInitialMount.current = true;
    }

    if (!video?.sourceUrl || !video?.duration) {
      return;
    }

    const fps = video?.details?.fps || 30;
    const durationInFrames =
      video?.configuration?.durationInFrames ||
      Math.round(video.duration * fps);

    if (video?.version === "v2") {
      configureRevisionTimeline();
    } else {
      configureNewTimeline(durationInFrames, fps);
    }

    setIsEditorLoaded(true);
  }, [
    video?.id,
    video?.sourceUrl,
    video?.duration,
    video?.details?.fps,
    video?.configuration?.durationInFrames,
    video?.version,
    configureNewTimeline,
    configureRevisionTimeline,
    setTracks,
  ]);

  const setApplyToAllForCurrentItem = (value: boolean) => {
    if (!selectedItem) return;

    setApplyToAllMap((prev) => ({
      ...prev,
      [selectedItem]: value,
    }));
  };

  const clearPollData = useCallback(
    (intervalId: any, videoDownloadUrl: string | undefined) => {
      setIsGenerating(false);
      clearInterval(intervalId);
      setPollingIntervalId(null);
      setGenerationProgress(0);
      setVideoDownloadUrl(videoDownloadUrl);
    },
    []
  );

  const startPolling = useCallback(async () => {
    const pollInterval = 5000;

    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
    }

    const intervalId = setInterval(async () => {
      try {
        const data = await monitorVideoGeneration(video.id);

        const { downloadUrl, progress, message } = data;

        if (message) {
          sendErrorNotification(message.body);
          setIsGenerating(false);
          setPollingIntervalId(null);
          clearPollData(intervalId, undefined);
        }

        setGenerationProgress(progress || 0);

        if (downloadUrl) {
          setGenerationProgress(100);
          clearPollData(intervalId, downloadUrl);
          setPollingIntervalId(null);
        }
      } catch (error) {
        clearPollData(intervalId, undefined);
        throw error;
      }
    }, pollInterval);

    setPollingIntervalId(intervalId);

    try {
      const data = await monitorVideoGeneration(video.id);
      setGenerationProgress(data.progress || 0);

      if (data.downloadUrl) {
        setVideoDownloadUrl(data.downloadUrl);
        clearPollData(intervalId, data.downloadUrl);
      }
    } catch (error) {
      sendErrorNotification("Kunne ikke overvåke videogenerering.");
    }
  }, [clearPollData, monitorVideoGeneration, pollingIntervalId, video.id]);

  useEffect(() => {
    return () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
    };
  }, [pollingIntervalId]);

  const generateVideo = useCallback(async () => {
    if (!video.id) return;

    setGenerationProgress(0);
    setVideoDownloadUrl(undefined);
    setIsGenerating(true);

    //No saves has been made yet (1 - default)
    if (video?.revisions?.length === 1) {
      const calculatedDuration = calculateTotalDurationInFrames(tracks);

      const config = {
        durationInFrames: calculatedDuration,
        fps,
      };

      await saveVideo(config, tracks, video.id);
    }

    try {
      const download = await downloadVideo(video.id);

      if (download?.error) {
        throw new Error(download.error);
      }

      const { status, downloadUrl } = download?.data as any;

      if (status === "download") {
        setVideoDownloadUrl(downloadUrl);
        setIsGenerating(false);
      } else {
        startPolling();
      }
    } catch (e) {
      setIsGenerating(false);
      const message = e as ExportError;
      setDisplayError(message);
      sendErrorNotification(message.body);
    }
  }, [
    downloadVideo,
    fps,
    saveVideo,
    startPolling,
    tracks,
    video.id,
    video?.revisions?.length,
  ]);

  const findSelectedItem = (): any => {
    if (!selectedItem) return undefined;

    for (const track of tracks) {
      const foundItem = track.items.find((item) => item.id === selectedItem);
      if (foundItem) return foundItem;
    }

    return undefined;
  };

  const selectedItemObject = findSelectedItem();

  const getSelectedItemSettings = (): ItemSettings => {
    if (!selectedItemObject) {
      return { ...defaultItemSettings };
    }

    const position = {
      x: selectedItemObject.position?.x ?? defaultItemSettings.position.x,
      y: selectedItemObject.position?.y ?? defaultItemSettings.position.y,
    };

    const rotation = selectedItemObject.rotation;

    const configuration = selectedItemObject.configuration;

    const size = selectedItemObject.size;

    let text = "";

    if (selectedItemObject.type === "text") {
      const textItem = selectedItemObject as TextItem;
      text = textItem.text || defaultItemSettings.content.text;
    }

    return {
      content: {
        text,
      },
      position,
      rotation,
      size,
      configuration,
    };
  };

  const updateContentSettings = (content: Partial<ContentSettings>) => {
    if (!selectedItem || !selectedItemObject) return;

    if (selectedItemObject.type === "text") {
      changeItem(selectedItem, (existingItem) => {
        if (existingItem.type !== "text") return existingItem;

        return {
          ...existingItem,
          text: content.text !== undefined ? content.text : existingItem.text,
        };
      });
    }
  };

  const updatePositionSettings = (position: Partial<PositionSettings>) => {
    if (!selectedItem || !selectedItemObject) return;

    if (applyToAll) {
      tracks.forEach((track) => {
        track.items.forEach((item) => {
          changeItem(item.id, (existingItem: any) => ({
            ...existingItem,
            position: {
              ...existingItem.position,
              x:
                position.x !== undefined ? position.x : existingItem.position.x,
              y:
                position.y !== undefined ? position.y : existingItem.position.y,
            },
          }));
        });
      });
    } else {
      changeItem(selectedItem, (existingItem: any) => ({
        ...existingItem,
        position: {
          ...existingItem.position,
          x: position.x !== undefined ? position.x : existingItem.position.x,
          y: position.y !== undefined ? position.y : existingItem.position.y,
        },
      }));
    }
  };

  const updateStrokeSettings = (strokeInput: Partial<StrokeSettings>) => {
    // Your existing guard - ensure it aligns with how `applyToAll` should work
    // if no item is selected. If applyToAll can work without a selectedItem,
    // this guard might need adjustment: e.g. if (!applyToAll && (!selectedItem || !selectedItemObject)) return;
    if (!selectedItem || !selectedItemObject) return;

    const processColor = (colorResult: any): string => {
      if (typeof colorResult !== "object" || colorResult === null) {
        return String(colorResult); // Ensure it's a string if not a typical color object
      }
      if (colorResult.rgb) {
        const { r, g, b, a = 1 } = colorResult.rgb;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      return typeof colorResult.hex === "string"
        ? colorResult.hex
        : String(colorResult); // Fallback
    };

    const colorToApply =
      strokeInput.color !== undefined
        ? typeof strokeInput.color === "object"
          ? processColor(strokeInput.color)
          : String(strokeInput.color) // Ensure it's a string
        : undefined;

    // This common logic correctly handles updating or creating the stroke object
    const getNewStrokeObject = (
      currentConfig: TextConfigurationSettings | undefined
    ) => {
      // Use existing stroke if available, otherwise use the default configuration as a base
      // Your DEFAULT_TEXT_CONFIGURATION.stroke is guaranteed to be an object.
      const baseStroke =
        currentConfig?.stroke || DEFAULT_TEXT_CONFIGURATION.stroke;

      const newWidth =
        strokeInput.width !== undefined ? strokeInput.width : baseStroke.width;

      const newColor =
        colorToApply !== undefined ? colorToApply : baseStroke.color;

      return {
        width: newWidth,
        color: newColor,
      };
    };

    if (applyToAll) {
      tracks.forEach((track) => {
        // Add TrackType if available
        track.items.forEach((item) => {
          // Add ItemType if available
          if (item.type === "text") {
            changeItem(item.id, (existingItem: any) => {
              // Add ItemType
              if (existingItem.type !== "text" || !existingItem.configuration)
                return existingItem;

              const newStroke = getNewStrokeObject(existingItem.configuration);

              return {
                ...existingItem,
                configuration: {
                  ...existingItem.configuration,
                  stroke: newStroke,
                },
              };
            });
          }
        });
      });
    } else {
      // This branch implies selectedItem and selectedItemObject are valid and selectedItemObject.type === "text"
      if (
        selectedItem &&
        selectedItemObject &&
        selectedItemObject.type === "text"
      ) {
        changeItem(selectedItem, (existingItem: any) => {
          // Add ItemType
          if (existingItem.type !== "text" || !existingItem.configuration)
            return existingItem;

          const newStroke = getNewStrokeObject(existingItem.configuration);

          return {
            ...existingItem,
            configuration: {
              ...existingItem.configuration,
              stroke: newStroke, // Corrected: Was 'background'
            },
          };
        });
      }
    }
  };

  const updateRotationSettings = (
    rotation: Partial<typeof DEFAULT_ROTATION>
  ) => {
    if (!selectedItem || !selectedItemObject) return;

    if (applyToAll) {
      tracks.forEach((track) => {
        track.items.forEach((item) => {
          changeItem(item.id, (existingItem: any) => ({
            ...existingItem,
            rotation: {
              ...existingItem.rotation,
              degrees:
                rotation.degrees !== undefined
                  ? rotation.degrees
                  : existingItem.rotation.degrees,
            },
          }));
        });
      });
    } else {
      changeItem(selectedItem, (existingItem: any) => ({
        ...existingItem,
        rotation: {
          ...existingItem.rotation,
          degrees:
            rotation.degrees !== undefined
              ? rotation.degrees
              : existingItem.rotation.degrees,
        },
      }));
    }
  };

  const updateAlignmentSettings = useCallback(
    (
      alignmentUpdate: Partial<{
        horizontal: string;
        vertical: string;
      }>
    ) => {
      if (!selectedItem || !selectedItemObject) return;

      if (applyToAll) {
        tracks.forEach((track) => {
          track.items.forEach((item) => {
            if (item.type === "text") {
              changeItem(item.id, (existingItem) => {
                if (existingItem.type !== "text") return existingItem;

                return {
                  ...existingItem,
                  configuration: {
                    ...existingItem.configuration,
                    alignment: {
                      ...existingItem.configuration.alignment,
                      ...(alignmentUpdate.horizontal !== undefined && {
                        horizontal: alignmentUpdate.horizontal,
                      }),
                      ...(alignmentUpdate.vertical !== undefined && {
                        vertical: alignmentUpdate.vertical,
                      }),
                    },
                  },
                };
              });
            }
          });
        });
      } else {
        if (selectedItemObject.type === "text") {
          changeItem(selectedItem, (existingItem) => {
            if (existingItem.type !== "text") return existingItem;

            return {
              ...existingItem,
              configuration: {
                ...existingItem.configuration,
                alignment: {
                  ...existingItem.configuration.alignment,
                  ...(alignmentUpdate.horizontal !== undefined && {
                    horizontal: alignmentUpdate.horizontal,
                  }),
                  ...(alignmentUpdate.vertical !== undefined && {
                    vertical: alignmentUpdate.vertical,
                  }),
                },
              },
            };
          });
        }
      }
    },
    [selectedItem, selectedItemObject, applyToAll, tracks, changeItem]
  );

  const updateGoogleFontsLink = useCallback(
    (selectedFont: { family: string }) => {
      if (!selectedFont?.family) return;

      const linkElement = document.getElementById(
        "editor-preview-fonts-link"
      ) as HTMLLinkElement;

      if (linkElement) {
        linkElement.href = `https://fonts.googleapis.com/css2?family=${selectedFont.family.replace(
          / /g,
          "+"
        )}&display=swap`;
      } else {
        const newLink = document.createElement("link");
        newLink.id = "editor-preview-fonts-link";
        newLink.rel = "stylesheet";
        newLink.href = `https://fonts.googleapis.com/css2?family=${selectedFont.family.replace(
          / /g,
          "+"
        )}&display=swap`;
        document.head.appendChild(newLink);
      }
    },
    []
  );

  const updateFontSettings = useCallback(
    (
      fontSettings: Partial<{
        family: string;
        size: number;
        decoration: string;
        lineHeight: number;
        variant: string;
        weight: string;
        color: string;
      }>
    ) => {
      if (!selectedItem || !selectedItemObject) return;

      if (fontSettings.family) {
        updateGoogleFontsLink({ family: fontSettings.family });
      }

      if (applyToAll) {
        tracks.forEach((track) => {
          track.items.forEach((item) => {
            if (item.type === "text") {
              changeItem(item.id, (existingItem) => {
                if (existingItem.type !== "text") return existingItem;

                return {
                  ...existingItem,
                  configuration: {
                    ...existingItem.configuration,
                    font: {
                      ...existingItem.configuration.font,
                      ...(fontSettings.family && {
                        family: fontSettings.family,
                      }),
                      ...(fontSettings.size && { size: fontSettings.size }),
                      ...(fontSettings.lineHeight && {
                        lineHeight: fontSettings.lineHeight,
                      }),
                      ...(fontSettings.decoration && {
                        decoration: fontSettings.decoration,
                      }),
                      ...(fontSettings.variant && {
                        variant: fontSettings.variant,
                      }),
                      ...(fontSettings.weight && {
                        weight: fontSettings.weight,
                      }),
                      ...(fontSettings.color && { color: fontSettings.color }),
                    },
                  },
                };
              });
            }
          });
        });
      } else {
        if (selectedItemObject.type === "text") {
          changeItem(selectedItem, (existingItem) => {
            if (existingItem.type !== "text") return existingItem;

            return {
              ...existingItem,
              configuration: {
                ...existingItem.configuration,
                font: {
                  ...existingItem.configuration.font,
                  ...(fontSettings.family && { family: fontSettings.family }),
                  ...(fontSettings.size && { size: fontSettings.size }),
                  ...(fontSettings.lineHeight && {
                    lineHeight: fontSettings.lineHeight,
                  }),
                  ...(fontSettings.decoration && {
                    decoration: fontSettings.decoration,
                  }),
                  ...(fontSettings.variant && {
                    variant: fontSettings.variant,
                  }),
                  ...(fontSettings.weight && { weight: fontSettings.weight }),
                  ...(fontSettings.color && { color: fontSettings.color }),
                },
              },
            };
          });
        }
      }
    },
    [
      selectedItem,
      selectedItemObject,
      applyToAll,
      tracks,
      changeItem,
      updateGoogleFontsLink,
    ]
  );

  const updateBackgroundSettings = (
    backgroundUpdate: Partial<BackgroundSettings>
  ) => {
    if (!selectedItem || !selectedItemObject) return;

    const processColor = (colorResult: any): string => {
      if (colorResult && colorResult.rgb) {
        const { r, g, b, a = 1 } = colorResult.rgb;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
      }

      return colorResult && colorResult.hex
        ? colorResult.hex
        : String(colorResult);
    };

    let colorToApply: string | undefined = undefined;
    if (backgroundUpdate.color !== undefined) {
      if (
        typeof backgroundUpdate.color === "object" &&
        backgroundUpdate.color !== null
      ) {
        colorToApply = processColor(backgroundUpdate.color);
      } else {
        colorToApply = String(backgroundUpdate.color);
      }
    }

    const radiusToApply: number | undefined = backgroundUpdate.radius;

    const updateItemBackground = (
      existingBackground: Partial<BackgroundSettings> | undefined
    ): Partial<BackgroundSettings> => {
      const newBackground = { ...(existingBackground || {}) };

      if (colorToApply !== undefined) {
        newBackground.color = colorToApply;
      }
      if (radiusToApply !== undefined) {
        newBackground.radius = radiusToApply;
      }
      return newBackground;
    };

    if (applyToAll) {
      tracks.forEach((track) => {
        track.items.forEach((item) => {
          if (item.type === "text") {
            changeItem(item.id, (existingItem: any) => {
              if (existingItem.type !== "text") return existingItem;
              return {
                ...existingItem,
                configuration: {
                  ...existingItem.configuration,
                  background: updateItemBackground(
                    existingItem.configuration.background
                  ),
                },
              };
            });
          }
        });
      });
    } else {
      if (selectedItemObject.type === "text") {
        changeItem(selectedItem, (existingItem: any) => {
          if (existingItem.type !== "text") return existingItem;
          return {
            ...existingItem,
            configuration: {
              ...existingItem.configuration,
              background: updateItemBackground(
                existingItem.configuration.background
              ),
            },
          };
        });
      }
    }
  };

  function calculateTotalDurationInFrames(tracks: any[]): number {
    if (!tracks || !Array.isArray(tracks) || tracks.length === 0) {
      return 300;
    }

    let maxEndFrame = 0;

    for (const track of tracks) {
      if (track.items && Array.isArray(track.items)) {
        for (const item of track.items) {
          const itemFrom = item.from || 0;
          const itemDuration = item.durationInFrames || 0;
          const itemEnd = itemFrom + itemDuration;

          if (itemEnd > maxEndFrame) {
            maxEndFrame = itemEnd;
          }

          if (
            item.type === "video" &&
            item.videoDurationInSeconds &&
            item.fps
          ) {
            const videoEndFrame =
              itemFrom + item.videoDurationInSeconds * item.fps;
            if (videoEndFrame > maxEndFrame) {
              maxEndFrame = videoEndFrame;
            }
          }
        }
      }
    }

    return Math.ceil(maxEndFrame) + 30;
  }

  const debouncedSave = useRef(
    debounce((tracksToSave, videoId) => {
      const calculatedDuration = calculateTotalDurationInFrames(tracksToSave);

      const config = {
        durationInFrames: calculatedDuration,
        fps,
      };

      saveVideo(config, tracksToSave, videoId);
    }, 5000)
  ).current;

  useEffect(() => {
    if (!video.id || tracks?.length === 0) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevTracksStringRef.current = JSON.stringify(tracks);
      return;
    }

    const currentTracksString = JSON.stringify(tracks);
    if (prevTracksStringRef.current !== currentTracksString) {
      debouncedSave(tracks, video.id);
      prevTracksStringRef.current = currentTracksString;
    }
  }, [tracks, debouncedSave, video.id]);

  const updateSizeSettings = useCallback(
    (sizeUpdate: Partial<{ width: number; height: number }>) => {
      if (!selectedItem || !selectedItemObject) return;

      if (applyToAll) {
        tracks.forEach((track) => {
          track.items.forEach((item) => {
            if (item.type === "text") {
              changeItem(item.id, (existingItem) => {
                if (existingItem.type !== "text") return existingItem;

                return {
                  ...existingItem,
                  size: {
                    ...existingItem.size,
                    ...(sizeUpdate.width !== undefined && {
                      width: sizeUpdate.width,
                    }),
                    ...(sizeUpdate.height !== undefined && {
                      height: sizeUpdate.height,
                    }),
                  },
                };
              });
            }
          });
        });
      } else {
        if (selectedItemObject.type === "text") {
          changeItem(selectedItem, (existingItem) => {
            if (existingItem.type !== "text") return existingItem;

            return {
              ...existingItem,
              size: {
                ...existingItem.size,
                ...(sizeUpdate.width !== undefined && {
                  width: sizeUpdate.width,
                }),
                ...(sizeUpdate.height !== undefined && {
                  height: sizeUpdate.height,
                }),
              },
            };
          });
        }
      }
    },
    [selectedItem, selectedItemObject, applyToAll, tracks, changeItem]
  );

  return (
    <SettingsContext.Provider
      value={{
        isEditorLoaded,
        loadEditor,
        applyToAll,
        setApplyToAllForCurrentItem,
        applyToAllMap,
        updateContentSettings,
        updatePositionSettings,
        getSelectedItemSettings,
        updateRotationSettings,
        updateFontSettings,
        updateStrokeSettings,
        updateBackgroundSettings,
        selectedItemId: selectedItem,
        updateAlignmentSettings,
        updateSizeSettings,
        bottomPanelHeight,
        setBottomPanelHeight,
        isGenerating,
        setIsGenerating,
        generateVideo,
        displayError,
        generationProgress,
        videoDownloadUrl,
        fonts,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
